import React, {useEffect, useRef, useState} from 'react';
import {Form, Link, NavLink, useActionData, useNavigate, useRouteLoaderData} from "react-router-dom";
import {useToast} from "../toast/ToastContext";
import {AuthService} from "../../../service/AuthService";
import {ActionResponse} from "../../../App";
import MainDesign from "../../../pages/MainDesign";
import {hasAdminGroup, hasAssistantGroup} from "../../../util/AuthUtil";
import {useUser} from "../../../context/UserContext";
import AnimatedLink from "../motion/AnimatedLink";
import {motion} from 'framer-motion';
import HeaderDropdownMenu from "./HeaderDropdownMenu";
import HeaderButtonOpenPhoneMenu from "./HeaderButtonOpenPhoneMenu";
import HeaderNavItem from "./HeaderNavItem";
import NotificationBell from "./NotificationBell";
import {User} from "../../../model/user/User";
import "./Header.css";

interface HeaderProps {
    mainPage?: HeaderLinkItem
    topItems: HeaderLinkItem[]
    dropdownItems: HeaderLinkItem[]
    logoCustomRedirect?: string
    loaderId: string
    useDarkTheme?: boolean
}

export interface HeaderLinkItem {
    title: string
    to?: string
    subItems?: HeaderLinkItem[]
}

export default function Header(props: HeaderProps) {
    const {mainPage, topItems, dropdownItems, logoCustomRedirect, loaderId, useDarkTheme} = props;
    const actionData = useActionData();

    // Try to get user from loader data first for immediate rendering
    const loaderUser = useRouteLoaderData(loaderId) as User | null;
    const {logout: contextLogout, groups: contextGroups, idToken: contextIdToken} = useUser();

    // Use loader data if available, otherwise fall back to context
    const groups = loaderUser?.groups || contextGroups;
    const idToken = loaderUser?.idToken || contextIdToken;

    const {showMessage} = useToast();
    const navigate = useNavigate();

    const [isMenuOpen, setMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const logoUrl = 'https://produkacja.s3.eu-central-1.amazonaws.com/web/logo/logo-500-500-raw.png';

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleClickOutside = (event: MouseEvent): void => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setDropdownOpen(false);
        }
    };

    const toggleMenu = () => {
        setMenuOpen(!isMenuOpen);
    };

    useEffect(() => {
        if (actionData) {
            const {body, status} = actionData as ActionResponse;
            showMessage(body);
            setDropdownOpen(false);
            if (status === 200) {
                // Call context logout to clear user state immediately
                contextLogout();
                navigate('/');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [actionData, contextLogout, navigate, showMessage]);

    return <div style={{
        zIndex: 2000,
        backgroundColor: useDarkTheme ? "#1a1a2e" : "white",
        overflowX: "visible",
        borderBottom: useDarkTheme
            ? "1px solid rgba(255, 255, 255, 0.06)"
            : "1px solid rgba(42, 51, 66, 0.15)",
    }}>
        <MainDesign minHeight="0" removeSectionOverflowHidden={true} hideBackgroundImage={true}
                    minHeightChildrenDiv={'56px'} disableAnimation={true} useSolidGradientTop={useDarkTheme}>

            {/* ── Desktop header ── */}
            <div className="d-none d-lg-flex align-items-center" style={{
                height: '56px',
                maxWidth: '1160px',
                margin: '0 auto',
                padding: '0 2rem',
                gap: '2rem',
                overflow: 'visible',
            }}>
                {/* Logo + Wordmark */}
                <NavLink to={logoCustomRedirect ?? '/'} className="header-logo-link">
                    <motion.div
                        style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
                        whileHover={{scale: 1.03}}
                        transition={{duration: 0.15}}
                    >
                        <img
                            className='img-fluid lazyload'
                            style={{height: "36px", width: '36px'}}
                            data-src={logoUrl}
                            alt='logo'
                        />
                        <span className="header-wordmark">Produkacja</span>
                    </motion.div>
                </NavLink>

                {/* Spacer */}
                <div style={{flex: 1}}/>

                {/* Nav links */}
                <nav style={{display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                    {mainPage && (
                        <DesktopNavLink to={mainPage.to || '#'} useDarkTheme={useDarkTheme} end>
                            {mainPage.title}
                        </DesktopNavLink>
                    )}
                    {topItems.map(function (item) {
                        if (item.subItems && item.subItems.length > 0) {
                            return <HeaderNavItem item={item} key={item.title} useDarkTheme={useDarkTheme}/>;
                        }
                        return (
                            <DesktopNavLink key={item.title} to={item.to || '#'} useDarkTheme={useDarkTheme}>
                                {item.title}
                            </DesktopNavLink>
                        );
                    })}
                </nav>

                {/* Divider between nav and utilities */}
                {idToken && (
                    <div style={{
                        width: '1px',
                        height: '20px',
                        background: useDarkTheme
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(0,0,0,0.1)',
                    }}/>
                )}

                {/* Utility icons */}
                {!idToken && (
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                        <AnimatedLink to='/logowanie'
                                      className={useDarkTheme ? 'link-secondary-light' : 'link-dark'}
                                      style={{fontSize: '0.85rem', fontWeight: 500, textDecoration: 'none'}}
                                      state={{from: window.location.pathname}}
                                      bounceEffect>
                            Zaloguj się
                        </AnimatedLink>
                        <AnimatedLink to='/rejestracja'
                                      className='btn btn-sm btn-success text-success-light'
                                      style={{
                                          fontSize: '0.8rem',
                                          fontWeight: 600,
                                          padding: '0.4rem 1rem',
                                          borderRadius: '8px'
                                      }}
                                      bounceEffect>
                            Zarejestruj się
                        </AnimatedLink>
                    </div>
                )}
                {idToken && (
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'visible'}}>
                        <NotificationBell readAnnouncementIds={loaderUser?.readAnnouncementIds} user={loaderUser}/>
                        <HeaderDropdownMenu dropdownRef={dropdownRef} toggleDropdown={toggleDropdown}
                                            dropdownOpen={dropdownOpen} groups={groups}
                                            dropdownItems={dropdownItems}/>
                    </div>
                )}
            </div>

            {/* ── Mobile header ── */}
            <div className="d-flex d-lg-none align-items-center justify-content-between" style={{
                height: '52px',
                padding: '0 1.25rem',
            }}>
                <NavLink to={logoCustomRedirect ?? '/'} className="header-logo-link">
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                        <img
                            className='img-fluid lazyload'
                            style={{height: "30px", width: '30px'}}
                            data-src={logoUrl}
                            alt='logo'
                        />
                        <span className="header-wordmark header-wordmark--mobile">Produkacja</span>
                    </div>
                </NavLink>
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                    {idToken && <NotificationBell readAnnouncementIds={loaderUser?.readAnnouncementIds} user={loaderUser}/>}
                    <HeaderButtonOpenPhoneMenu toggleMenu={toggleMenu}/>
                </div>
            </div>

            {/* ── Mobile slide-out menu ── */}
            <div
                className={`navbar-menu position-fixed top-0 start-0 bottom-0 w-75 mw-xs ${!isMenuOpen ? 'd-none' : ''}`}
                style={{zIndex: 9999}}
            >
                <div
                    className='navbar-backdrop position-fixed top-0 start-0 end-0 bottom-0 bg-dark'
                    style={{opacity: '75%'}}
                    onClick={toggleMenu}
                />
                <nav
                    className='position-relative h-100 w-100 d-flex flex-column justify-content-between p-6 overflow-auto'
                    style={{background: useDarkTheme ? 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1e 100%)' : '#2A3342'}}>
                    <div className='d-flex align-items-center'>
                        <Link className='me-auto h4 mb-0 text-decoration-none' to={logoCustomRedirect ?? '/'}>
                            <img
                                loading='lazy'
                                className='img-fluid'
                                style={{height: "60px"}}
                                src={logoUrl}
                                alt=''
                            />
                        </Link>
                        <Link className='navbar-close text-secondary-light' to='#' onClick={toggleMenu}>
                            <svg
                                width={24}
                                height={24}
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M6 18L18 6M6 6L18 18'
                                    stroke='currentColor'
                                    strokeWidth={2}
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </Link>
                    </div>
                    <div className='py-16'>
                        <ul className='nav nav-pills flex-column'>
                            {
                                mainPage &&
                                <HeaderNavItem item={mainPage} mainPage={true} burgerMenu={true} onClick={toggleMenu}
                                               useDarkTheme={useDarkTheme}/>
                            }
                            {
                                topItems.map((item) => <HeaderNavItem item={item} burgerMenu={true} key={item.title}
                                                                      onClick={toggleMenu}
                                                                      useDarkTheme={useDarkTheme}/>)
                            }
                        </ul>
                    </div>
                    <div className='row align-items-center g-6'>
                        {
                            !idToken && <>
                                <div className='col-12'>
                                    <Link
                                        className='fs-11 d-block w-100 link-secondary-light text-center fw-medium'
                                        to='/logowanie'
                                        state={{from: window.location.pathname}}
                                    >
                                        Zaloguj się
                                    </Link>
                                </div>
                                <div className='col-12'>
                                    <Link
                                        className='btn btn-sm btn-success w-100 text-success-light shadow'
                                        to='/rejestracja'
                                    >
                                        Zarejestruj się
                                    </Link>
                                </div>
                            </>
                        }
                        {
                            idToken && <>
                                {
                                    (hasAdminGroup(groups) || hasAssistantGroup(groups)) &&
                                    <Link
                                        className='fs-11 d-block w-100 link-secondary-light text-center fw-medium'
                                        to='/admin'
                                    >
                                        Panel admina
                                    </Link>
                                }
                                <div className='col-12'>
                                    <Link
                                        className='fs-11 d-block w-100 link-secondary-light text-center fw-medium'
                                        to='/produkt'
                                        onClick={toggleMenu}
                                    >
                                        Dashboard
                                    </Link>
                                </div>
                                {
                                    dropdownItems.map((item) => <div className='col-12'
                                                                     key={`${item.to || item.title}-${item.title}`}>
                                        <Link
                                            className='fs-11 d-block w-100 link-secondary-light text-center fw-medium'
                                            to={item.to || '#'}
                                        >
                                            {item.title}
                                        </Link>
                                    </div>)
                                }
                                <div className='col-12'>
                                    <Form method="post" action="/logout">
                                        <button className='btn btn-sm btn-warning w-100 text-success-light shadow'>
                                            Wyloguj się
                                        </button>
                                    </Form>
                                </div>
                            </>
                        }
                    </div>
                </nav>
            </div>
        </MainDesign>
    </div>
}

// ─── Desktop nav link with pill hover ───────────────────────────────────────

function DesktopNavLink({to, children, useDarkTheme, end}: {
    to: string,
    children: React.ReactNode,
    useDarkTheme?: boolean,
    end?: boolean
}) {
    const themeClass = useDarkTheme ? '' : ' header-nav-pill--light';
    return (
        <NavLink
            to={to}
            end={end}
            style={{textDecoration: 'none'}}
            className={function ({isActive}) {
                const base = isActive ? 'header-nav-pill header-nav-pill--active' : 'header-nav-pill';
                return base + themeClass;
            }}
        >
            {children}
        </NavLink>
    );
}

// ─── Logout action ──────────────────────────────────────────────────────────

export async function logoutAction() {
    try {
        await AuthService.signOut();
        return {
            status: 200,
            body: "Pomyślnie wylogowano."
        }
    } catch (error) {
        return {
            status: 500,
            body: "Wystąpił błąd. Spróbuj ponownie."
        }
    }
}
